import { PlusOutlined, CheckCircleOutlined, UserOutlined, InteractionOutlined } from "@ant-design/icons";
import { Button, Col, Row, Table, Tag, message } from "antd";
import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  Customer,
  RowSelection,
  TransferRequest,
} from "../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import ExportData from "../components/ExportData";
import {
  capitalizeFirstLetter,
  generateNameFromCustomer,
  injectKey,
  loadSavedFilters,
  request,
  responseHandler,
  transferTypes,
  getStateColor,
  formatSnakeCase,
  fetchEnumOptions,
  showTotalPagination,
} from "../helpers/utilities";
import {
  SERACH_TRANSFER_REQUEST,
  GET_TRANSFER_REQUEST_STATE_ENUM,
  GET_TRANSFER_REQUEST_SORT_FIELDS,
  GET_TRANSFER_TYPE_ENUM,
} from "../gql/transfer-requests";
import { ALL_USERS_QUERY } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { PageToolbar } from "../components/PageToolbar";
import {
  hasActiveFilters,
  clearAllFilters,
  parseQueryParams,
  buildQueryString,
} from "../components/TableFilters";
import { generateDisplayErrorMessage } from "../helpers/utilities";




interface State {
  transferRequests: TransferRequest[];
  isLoading: boolean;
  transferRequestsCount: number;
  page: number;
  pageSize: number;
  q: string;
  state: string[];
  forCurrentUser: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
  selectedIdsToExport: React.Key[];
  stateOptions: Array<{ value: string; label: string; color?: string; }>;
  selectedRequestedBy: string[];
  requestedByOptions: Array<{ value: string; label: string; }>;
  isSearchingUsers: boolean;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string; }>;
  selectedTransferTypes: string[];
  transferTypeOptions: Array<{ value: string; label: string; }>;
}

export function getColumns<T extends TransferRequest = TransferRequest>(
  requests: TransferRequest[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Request Date",
      dataIndex: "requestDate",
      key: "requestDate",
      render(text: string, record: TransferRequest): JSX.Element {
        return (
          <span>
            {record.requestDate
              ? moment(record.requestDate, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Requestor Name",
      dataIndex: "requestorName",
      key: "requestorName",
      render(text: string, record: TransferRequest): JSX.Element {
        if (record.company) {
          return (
            <NavLink
              to={{
                pathname: `transfer-requests/${record.id}`,
                state: { type: "transfer", requests },
              }}
            >
              {record.company?.name}
            </NavLink>
          );
        }
        if (record.customer) {
          return (
            <NavLink
              to={{
                pathname: `transfer-requests/${record.id}`,
                state: { type: "transfer", requests },
              }}
            >
              {generateNameFromCustomer(record.customer as Customer)}
            </NavLink>
          );
        }

        if (record.toCompanyName) {
          return (
            <NavLink to={`transfer-requests/${record.id}`}>
              {record.toCompanyName}
            </NavLink>
          );
        }

        if (record.fromCompanyName) {
          return (
            <NavLink to={`transfer-requests/${record.id}`}>
              {record.fromCompanyName}
            </NavLink>
          );
        }

        return <NavLink to={`transfer-requests/${record.id}`}>n/a</NavLink>;
      },
    },
    {
      title: "Status",
      dataIndex: "transferStatus",
      key: "transferStatus",
      render(transferStatus: string): JSX.Element {
        return (
          <Tag
            color={getStateColor(transferStatus)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(transferStatus)}
          </Tag>
        );
      },
    },
    {
      title: "Trustee Name",
      dataIndex: "trustee",
      key: "trustee",
      render(text: string, record: TransferRequest): JSX.Element {
        return (
          <span>
            {record.trusteeScheme?.trustee?.name ||
              record.trusteeScheme?.name ||
              "N/A"}
          </span>
        );
      },
    },
    {
      title: "Transfer Type",
      dataIndex: "transferType",
      key: "transferType",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{transferTypes[record.transferType]}</span>;
      },
    },
    {
      title: "From Product",
      dataIndex: "fromProduct",
      key: "fromProduct",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{record.fromProduct?.name || "N/A"}</span>;
      },
    },
    {
      title: "To Product",
      dataIndex: "toProduct",
      key: "toProduct",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{record.toProduct?.name || "N/A"}</span>;
      },
    },
    {
      title: "Requested By",
      dataIndex: "user",
      key: "user",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{record.user?.name}</span>;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render(text: string, record: TransferRequest): JSX.Element {
        return <span>{record.amount || "N/A"}</span>;
      },
    },
  ];
}


export default class Transfers extends React.Component<
  RouteComponentProps,
  State
> {
  _isMounted = false;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      transferRequests: [],
      transferRequestsCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      state: [],
      forCurrentUser: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
      selectedIdsToExport: [],
      stateOptions: [],
      selectedRequestedBy: [],
      requestedByOptions: [],
      isSearchingUsers: false,
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      selectedTransferTypes: [],
      transferTypeOptions: [],
    };

    this.nextPage = this.nextPage.bind(this);
    this.redirectToNewtransferRequestPage =
      this.redirectToNewtransferRequestPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  redirectToNewtransferRequestPage = (): void => {
    this.props.history.push("/transfer-requests/new");
  };

  triggerImmediateSearch = async (): Promise<void> => {
    const {
      q,
      page,
      pageSize,
      state,
      selectedRequestedBy,
      selectedTransferTypes,
      sortField,
      sortDirection,
    } = this.state;
    interface Response {
      count: number;
      results: TransferRequest[];
    }

    this.setState({ isLoading: true });

    try {
      const r: AxiosResponse = await request({
        query: SERACH_TRANSFER_REQUEST,
        variables: {
          q,
          page,
          numberPerPage: pageSize,
          states: state,
          requestedByIds: selectedRequestedBy,
          transferTypes: selectedTransferTypes,
          orderBy: { field: sortField, direction: sortDirection },
        },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "transferRequestSearch",
      );

      if (handled) {
        message.error("Failed To Load Transfer Requests");
      } else {
        this.syncBrowserQueryString();
        this.setState({
          isLoading: false,
          transferRequests: mutation.results,
          transferRequestsCount: mutation.count,
        });
      }
    } catch (error) {
      this.setState({
        isLoading: false,
      });

      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  syncBrowserQueryString = (): void => {
    const queryStringResult = buildQueryString(
      {
        ...this.state,
        selectedStates: this.state.state,
      } as any,
      {
        includeSort: true,
        includeRequestedBy: true,
        includeTransferType: true,
      }
    );
    this.props.history.replace(queryStringResult);
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      { page, pageSize: pageSize },
      async () => {
        if (this.state.selectedFieldsToDisplay.length < 1) {
          await this.triggerImmediateSearch();
        }
      },
    );
  }

  fetchStateEnums = async (): Promise<void> => {
    const stateOptions = await fetchEnumOptions(
      GET_TRANSFER_REQUEST_STATE_ENUM,
      {
        includeColor: true,
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch state enums",
      },
    );

    if (this._isMounted) {
      this.setState({ stateOptions });
    }
  };

  fetchUsers = async (q?: string): Promise<void> => {
    try {
      if (q) {
        this.setState({ isSearchingUsers: true });
      }
      const response: AxiosResponse = await request({
        query: ALL_USERS_QUERY,
        variables: { page: 1, q: q || undefined },
      });

      const results = response.data?.data?.users?.results || [];

      const requestedByOptions = results.map((user: any) => ({
        value: user.id,
        label: user.name,
      }));

      if (this._isMounted) {
        this.setState({ requestedByOptions, isSearchingUsers: false });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to fetch users:", error);
      if (this._isMounted) {
        this.setState({ isSearchingUsers: false });
      }
    }
  };

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_TRANSFER_REQUEST_SORT_FIELDS,
      {
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch sort options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  fetchTransferTypeEnums = async (): Promise<void> => {
    const transferTypeOptions = await fetchEnumOptions(
      GET_TRANSFER_TYPE_ENUM,
      {
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch transfer type enums",
      },
    );

    if (this._isMounted) {
      this.setState({ transferTypeOptions });
    }
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);


  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeRequestedBy: true,
      includeSort: true,
      includeTransferType: true,
    });

    this.setState(
      {
        q: parsed.q || "",
        page: parsed.page || 1,
        state: parsed.selectedStates || [],
        selectedRequestedBy: parsed.selectedRequestedBy || [],
        selectedTransferTypes: parsed.selectedTransferTypes || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
      },
      callback,
    );
  };

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      transferRequests: data.results as TransferRequest[],
      selectedFieldsToDisplay: fields,
      transferRequestsCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      {
        removedField: field,
        selectedFieldsToDisplay: fields,
      },
      () => {
        localStorage.setItem(
          "transferRequest",
          JSON.stringify(this.state.selectedFieldsToDisplay),
        );
      },
    );

    if (fields.length === 0) this.triggerDelayedSearch();
  };

  handleStateFilter = (states: string[]) => {
    this.setState({ state: states, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  handleRequestedByFilter = (values: string[]) => {
    this.setState({ selectedRequestedBy: values, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  handleRequestedBySearch = debounce((value: string): void => {
    this.fetchUsers(value);
  }, 300);

  handleTransferTypeFilter = (values: string[]) => {
    this.setState({ selectedTransferTypes: values, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.triggerImmediateSearch();
      },
    );
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await Promise.all([
      this.fetchStateEnums(),
      this.fetchUsers(),
      this.fetchSortFieldOptions(),
      this.fetchTransferTypeEnums(),
    ]);

    this.mergeWithLocalState(() => {
      if (loadSavedFilters("transferRequest").length < 1) {
        this.triggerImmediateSearch();
      }
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  render(): JSX.Element | null {
    const {
      transferRequests,
      transferRequestsCount,
      page,
      pageSize,
      q,
      selectedFieldsToDisplay,
      removedField,
      selectedIdsToExport,
      isLoading,
    } = this.state;

    // rowSelection object indicates the need for row selection
    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const isFiltering = hasActiveFilters(
      {
        q,
        page,
        selectedStates: this.state.state,
        selectedRequestedBy: this.state.selectedRequestedBy,
        selectedTransferTypes: this.state.selectedTransferTypes,
        sortField: this.state.sortField,
        sortDirection: this.state.sortDirection,
      },
      {
        includeSort: true,
        includeRequestedBy: true,
        includeTransferType: true,
      },
    );

    return (
      <Fragment>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="transferRequest"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
        />
          <Card>
            <PageToolbar
              advancedSearch={{
                onOpen: this.toggleAdvancedSearchModel,
                activeFields: selectedFieldsToDisplay,
                onFieldRemove: this.handleOnFieldRemove,
              }}
              onSearchChange={this.handleSearchInput}
              searchValue={q}
              searchPlaceholder="Search by trustee or company name, petra id, ssnit number"
              actions={
                <ExportData
                  model={"transferRequest"}
                  dataArray={this.state.transferRequests}
                  size={"large"}
                  customIds={selectedIdsToExport}
                />
              }
              primaryAction={
                <EntitledSection
                  entitlement={entitlements.TransferRequests.create}
                >
                  <Button
                    className="base-button"
                    size="large"
                    data-testid="create-transfer-request-button"
                    onClick={this.redirectToNewtransferRequestPage}
                  >
                    <PlusOutlined />
                    Create Transfer Request
                  </Button>
                </EntitledSection>
              }
              totalCount={transferRequestsCount}
              currentDataCount={transferRequests.length}
              page={page}
              pageSize={pageSize}
              selectedCount={selectedIdsToExport.length}
              filters={
                <PageToolbar.Filters
                  isFiltering={isFiltering}
                  onClear={() => clearAllFilters(this)}
                  configs={[
                    {
                      label: "Status",
                      options: this.state.stateOptions,
                      selectedValues: this.state.state,
                      onChange: this.handleStateFilter,
                      width: 180,
                      icon: <CheckCircleOutlined />,
                    },
                    {
                      label: "Requested By",
                      options: this.state.requestedByOptions,
                      selectedValues: this.state.selectedRequestedBy,
                      onChange: this.handleRequestedByFilter,
                      onSearch: this.handleRequestedBySearch,
                      loading: this.state.isSearchingUsers,
                      width: 220,
                      icon: <UserOutlined />,
                    },
                    {
                      label: "Transfer Type",
                      options: this.state.transferTypeOptions,
                      selectedValues: this.state.selectedTransferTypes,
                      onChange: this.handleTransferTypeFilter,
                      width: 200,
                      icon: <InteractionOutlined />,
                    },
                  ]}
                  sort={{
                    field: this.state.sortField,
                    direction: this.state.sortDirection,
                    options: this.state.sortFieldOptions,
                    onChange: this.handleSortChange,
                  }}
                />
              }
            modelName="transfer requests"
          />
          <Fragment>
            <Row>
                <Col span={24}>
                  <Table
                    loading={isLoading}
                    scroll={{ x: 500 }}
                    columns={getColumns(transferRequests)}
                    dataSource={injectKey<TransferRequest>(
                      transferRequests,
                      "id",
                    )}
                    pagination={{
                      current: page,
                      defaultPageSize: pageSize,
                      total: transferRequestsCount,
                      onChange: this.nextPage,
                      showTotal: showTotalPagination,
                    }}
                    rowSelection={rowSelection}
                  />
                </Col>
              </Row>
            </Fragment>
          </Card>
      </Fragment>
    );
  }
}
